import React, {useState, useEffect, useRef, useCallback} from "react";
import { generateClient } from "aws-amplify/api"
import {
  acord25ScanByCertificate,
  updateACORD25SCAN,
  onUpdateACORD25SCAN,
  tableRowAcord25ScanByCertificate
} from "graphql/vendor"

import { getUrl } from "@aws-amplify/storage";
const API = generateClient();

export default function useScanData(certificateID, repeatOnPending, showTableDataOnly) {
  const [scanData, setScanData] = useState()
  const subRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const getScanData = useCallback(async (callback, isPolling = false) => {
    let result;
    const query = showTableDataOnly ? tableRowAcord25ScanByCertificate : acord25ScanByCertificate;
    try {
      result = await API.graphql({
        query: query,
        variables: {
          certificateID,
          sortDirection: "DESC",
          limit: 1
        },
      })

      // Log GraphQL errors but don't fail the request
      if (result?.errors && result.errors.length > 0) {
        console.warn('GraphQL returned errors while fetching scan data:', result.errors);
      }
    } catch (error) {
      // Only log as error if it's not a "no data found" scenario
      if (error?.errors?.[0]?.errorType !== 'Unauthorized' && !error?.errors?.[0]?.message?.includes('not authorized')) {
        console.error('Error fetching scan data:', error);
      } else {
        console.debug('No scan data found for certificate (this is normal for new certificates):', certificateID);
      }
      result = { data: null, errors: [error] };
    }
    if(result?.data?.acord25ScanByCertificate?.items?.length) {
      const scan = result.data.acord25ScanByCertificate.items[0]
      if(scan && scan.status === "COMPLETED") {
        const { url } = await getUrl({
          path: `protected/scanning/input/${certificateID}/${scan.id}/${scan.acord25File}`
        })
        scan.files = [{name: scan.acord25File, content: url.toString()}]
      }
      await setScanData(scan)
      if(scan && scan.status === "PENDING" && repeatOnPending && !isPolling) {
        // Unsubscribe from any existing subscription
        if (subRef.current) {
          subRef.current.unsubscribe();
        }
        
        // Clear any existing polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        
        // Set up subscription for real-time updates
        subRef.current = API.graphql({
          query: onUpdateACORD25SCAN,
          variables: {
            filter: {
              id: { eq: scan.id }
            }
          }
        }).subscribe({
          next: ({ value }) => {
            if(value?.data?.onUpdateACORD25SCAN) {
              console.log('Scan status update received:', value.data.onUpdateACORD25SCAN);
              setScanData({
                ...scan,
                ...value.data.onUpdateACORD25SCAN,
              })

              if(value.data.onUpdateACORD25SCAN.status !== "PENDING") {
                console.log('Scan completed with status:', value.data.onUpdateACORD25SCAN.status);
                subRef.current.unsubscribe();
                subRef.current = null;
                // Clear polling when status changes
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
              }
            }
          },
          error: (err) => {
            console.error('Subscription error:', err);
          }
        })
        
        // Also set up polling as a fallback (every 5 seconds)
        pollingIntervalRef.current = setInterval(() => {
          console.log('Polling for scan status update...');
          getScanData(null, true);
        }, 5000);
      } else {
        // Clear polling if status is not PENDING
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
      callback && callback()
    } else {
      setScanData(undefined)
    }
  }, [certificateID, repeatOnPending, showTableDataOnly]);

  async function discardScanData() {
    if(scanData?.id) {
      const result = await API.graphql({
        query: updateACORD25SCAN,
        variables: {
          input: {
            id: scanData.id,
            status: "CANCELED",
          }
        }
      })
      if(result?.data?.updateACORD25SCAN) {
        getScanData()
      }
    }
  }

  useEffect(() => {
    if(certificateID && certificateID !== "create") {
      getScanData()
      return () => {
        if(subRef.current) {
          subRef.current.unsubscribe();
          subRef.current = null;
        }
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    }
  }, [certificateID, getScanData])

  return {scanData, getScanData, discardScanData}
}
